
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Button, Flex, Input, Select, TableProps } from "antd";
import { CustomTabPaneContext, CustomTabPaneContextType } from "@/components/TabPane";
import { InspectionOrderItem, InspectionOrderStatus } from "@/types/inspection";
import { useTranslation } from "react-i18next";
import iconFolder from "@/assets/images/fileIcon/folder.svg";
import SText from "@/styles/SText";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import WMSAvatar from "@/styles/WMSAvatar";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import useInspactionEnum from "../../hooks/useInspactionEnum";
import { AnyObject } from "antd/es/_util/type";
import { InspectionOrderContext } from "./context";
import InspectItemForm from "./InspectItemForm";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import useDataList from "@/hooks/useDataList";
import { itemList } from "@/apis/inspection";
import { CommonListParams } from "@/types/common";
import { getLocationIdx } from "@/utils/wLocation";


const ItemList: FC = () => {
  const { pageId } = usePage();
  const { order, items } = useContext(InspectionOrderContext);
  const { onOpen } = useContext<CustomTabPaneContextType<InspectionOrderItem>>(CustomTabPaneContext);
  const { t } = useTranslation();
  const { getExistEnum, getCompline, InspectionStatusEnums } = useInspactionEnum();
  const [item, setItem] = useState<InspectionOrderItem | null>(null);

  const [keyword, setKeyword] = useState<string>('');
  const [status, setStatus] = useState<InspectionOrderStatus>();
  const abctl = useRef<AbortController | null>();
  const tableLayoutRef = useRef<TableLayoutRef>(null);

  const [list, total, loading, { fetchList }] = useDataList<InspectionOrderItem, [InspectionOrderStatus | undefined, CommonListParams]>({ fetchApi: itemList })

  useEffect(() => {
    if (pageId === PageId.INSPECTION_ITEM) fetch(0);
  }, [pageId]);

  useEffect(() => abctl.current?.abort(), [])


  const fetch = (offset: number) => {
    if (pageId !== PageId.INSPECTION_ITEM) return;
    if (offset === 0) tableLayoutRef.current?.resetTablePage();
    let take = tableLayoutRef.current?.pageSize || DEFAULT_PAGE_SIZE;
    abctl.current?.abort();
    const controller = new AbortController();
    abctl.current = controller;
    fetchList(status, { body: keyword, pagination: { take, offset }, controller })
  };

  const columns: TableProps<InspectionOrderItem>['columns'] = [
    {
      title: t("warehouse.locationIndex"),
      key: "locationIdx",
      width: 160,
      align: "left",
      fixed: "left",
      render: (_, record) => {
        const idx = getLocationIdx(record.itemLocation?.location);
        return idx && <SText text={idx} />
      }
    },
    {
      title: t("product.code"),
      key: "productCode",
      width: 120,
      render: (_, record) => record.product?.code && <SText text={record.product.code} />
    },
    {
      title: t("product.name"),
      key: "productName",
      width: 150,
      render: (_, record) => record.product?.name && <SText text={record.product.name} />
    },
    {
      title: t("order.brand"),
      key: "productModel",
      width: 120,
      render: (_, record) => record.product?.model && <SText text={record.product.model} />
    },
    {
      title: t("product.uom"),
      key: "uom",
      width: 80,
      align: "center",
      render: (_, record) => record.product?.uom && <SText text={record.product.uom} />
    },
    {
      title: t("inspection.existenceState"),
      key: "existenceState",
      width: 100,
      align: "center",
      render: (_, record) => {
        let info = getExistEnum(record.existenceState);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: t("inspection.complianceState"),
      key: "complianceState",
      width: 100,
      align: "center",
      render: (_, record) => {
        let info = getCompline(record.complianceState);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: t("inspection.action"),
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => record.action && <SText text={record.action} />
    },
    {
      title: t("inspection.inspectedAt"),
      key: "inspectedAt",
      width: 100,
      align: "center",
      render: (_, record) => record.inspectedAt && <SText text={dateFormat(dayjs(record.inspectedAt))} />
    },
    {
      title: t("inspection.inspector"),
      key: "inspector",
      width: 160,
      align: "center",
      render: (_, item) => {
        let userData = item.inspector;
        return (
          userData && <Flex justify="center" gap={6}>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} style={{ width: "auto" }} />
          </Flex>
        )
      },
    },
    // {
    //   title: t("inspection.remark"),
    //   key: "remark",
    //   width: 120,
    //   align: "center",
    //   render: (_, record) => record.remark && <SText text={record.remark} />
    // },
    {
      title: <SText text={t('common.attachment')} />,
      width: 120,
      key: "attachment",
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => onOpen("doc", record)}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      key: "opt",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => {
        let disable = [InspectionOrderStatus.REJECTED, InspectionOrderStatus.RESOLVED].includes(order?.status);
        return (
          <Fragment>
            <Button type="link" disabled={disable} onClick={() => setItem(record)} >{t('inspection.inspect')}</Button>
          </Fragment>
        )
      }
    }
  ];

  const headerChildren = (
    <Flex gap={8} justify="flex-end" style={{ width: "100%" }}>
      <Select allowClear style={{ width: 240 }} placeholder={t('inspection.plState')} value={status} options={InspectionStatusEnums} onChange={setStatus} />
      <Input.Search loading={loading} placeholder={t('order.searchPl')} enterButton value={keyword} onChange={e => setKeyword(e.target.value)} onSearch={() => fetch(0)} />
    </Flex>
  )

  return (
    <Fragment>
      <STableLayout
        tableLayoutRef={tableLayoutRef}
        pagination={pageId === PageId.INSPECTION_ITEM ? undefined : false}
        headerChildren={pageId === PageId.INSPECTION_ITEM ? headerChildren : null}
        total={pageId === PageId.INSPECTION_ITEM ? total : items.length}
        dataSource={pageId === PageId.INSPECTION_ITEM ? list : items}
        extHeight={pageId === PageId.INSPECTION_ITEM ? undefined : 100}
        columns={columns as AnyObject[]}
        scrollX={1000}
        handleChange={fetch}
        loading={pageId === PageId.INSPECTION_ITEM ? loading : undefined}
      />
      {item && <InspectItemForm open={!!item} item={item} onClose={() => setItem(null)} />}
    </Fragment>
  )
};

export default ItemList;